/**
 * @file MlrPrintChrome.tsx
 * @brief A4 print-layout chrome matching the exported MLR PDF.
 *
 * @details
 * Cover, contents, identity sheet, section headings, and running footer
 * follow BACKEND `mlrPdf.theme.mjs` (Times body, 0.75" margins, hairline
 * identity rows, footer meta). Footer uses `mt-auto` so it pins to the A4
 * bottom on short sheets — `flex-1` does not fill `min-height` leftover
 * (08-09-2026). Pages stay white in dark mode so editing feels like the
 * paper report.
 *
 * @author DES
 * @date 08-09-2026
 */

import type { ReactNode } from "react";
import { cn } from "@/shared/theme/cn";

/**
 * Cover boilerplate. Lives beside the cover it prints — the section model
 * covers the thirteen numbered sections, not the title sheet. (13-09-2026)
 */
const COVER_JURISDICTION =
  "Prepared in accordance with the governing rules and standards for expert evidence applicable to this jurisdiction";

const COVER_DISCLAIMER =
  "This report has been prepared for the Court. It contains the claimant's personal and medical information and has been produced in accordance with the applicable legal and professional standards governing expert evidence in this jurisdiction.";

/*
 * ⚠️ `MLR_PAPER_BUTTON` lived here — a squared, serif control drawn to look
 * like part of the document rather than like the app. Removed 18-09-2026.
 *
 * The reasoning held while there were three of them (§6 import, §13 MEDYX
 * fetch, §12 Sign): a shared house style, so they could not drift apart. The
 * first two went the same day — both re-ran something already automatic — and
 * a lone sharp-cornered serif button among rounded ones stops reading as a
 * convention and starts reading as a mistake.
 *
 * Controls on the sheet now use the app's own `Button`. The PAPER — the type,
 * the rules, the margins — is unchanged; it was only the controls that stopped
 * pretending to be printed.
 */

/** The sheet's own type family, for text that sits on it. */
export const MLR_PAPER_FONT = {
  fontFamily: '"Times New Roman", Times, Georgia, serif',
} as const;

/** Times 10.5pt / ~1.85 leading ≈ PDF 10pt + 9pt lineGap. */
export const MLR_BODY =
  "font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] leading-[1.85] text-[#1a1a1a]";

export const MLR_BODY_JUSTIFY = cn(MLR_BODY, "text-justify");

type SheetProps = {
  children: ReactNode;
  pageNumber: number;
  totalPages: number;
  claimantName: string;
  doctorName: string;
  reportDate: string;
  showFooter?: boolean;
  className?: string;
  id?: string;
};

export function MlrSheet({
  children,
  pageNumber,
  totalPages,
  claimantName,
  doctorName,
  reportDate,
  showFooter = true,
  className,
  id,
}: SheetProps) {
  return (
    <article
      id={id}
      data-mlr-page={id}
      style={{ fontFamily: '"Times New Roman", Times, Georgia, serif' }}
      className={cn(
        // `mlr-sheet` scopes the selection colour — see index.css. The global
        // rule paints selected text in `--foreground`, which is near-white in
        // dark mode and therefore invisible on a sheet that is white in both.
        "mlr-sheet relative mx-auto flex w-full max-w-[210mm] flex-col bg-white text-[#1a1a1a] shadow-[0_2px_12px_rgba(0,0,0,0.12)]",
        // 10mm below footer text ≈ PDF `footerY` 42pt (pageHeight − 36pt to type).
        "min-h-[297mm] px-[18mm] pb-[10mm] pt-[18mm]",
        className,
      )}
    >
      <div className={cn(MLR_BODY, "pb-8")}>{children}</div>
      {showFooter ? (
        <footer className="mt-auto shrink-0 border-t border-[#c8c8c8] pt-2">
          <div className="flex items-start justify-between gap-4 font-[family-name:Times_New_Roman,Times,serif] text-[8pt] text-[#444444]">
            <p className="min-w-0 truncate">
              Claimant: {claimantName} / Prepared by: {doctorName} / Date:{" "}
              {reportDate}
            </p>
            <p className="shrink-0">
              {pageNumber}  /  {totalPages}
            </p>
          </div>
        </footer>
      ) : null}
    </article>
  );
}

type CoverProps = {
  brand: string;
  claimantName: string;
  doctorName: string;
  qualifications: string;
  reportDate: string;
  pageNumber: number;
  totalPages: number;
};

export function MlrCoverPage({
  brand,
  claimantName,
  doctorName,
  qualifications,
  reportDate,
  pageNumber,
  totalPages,
}: CoverProps) {
  return (
    <MlrSheet
      id="mlr-cover"
      pageNumber={pageNumber}
      totalPages={totalPages}
      claimantName={claimantName}
      doctorName={doctorName}
      reportDate={reportDate}
      showFooter={false}
    >
      <div className="flex min-h-[257mm] flex-col">
        <p className="font-[family-name:Times_New_Roman,Times,serif] text-[12pt] font-bold">
          {brand}
        </p>
        <h1 className="mt-6 font-[family-name:Times_New_Roman,Times,serif] text-[22pt] font-bold leading-tight">
          Medico-Legal Report
        </h1>
        <div className="mt-3 h-px w-[120px] bg-[#1a1a1a]" />
        <p className="mt-4 max-w-[92%] font-[family-name:Times_New_Roman,Times,serif] text-[10pt] leading-snug text-[#444444]">
          {COVER_JURISDICTION}
        </p>

        <h2 className="mt-16 font-[family-name:Times_New_Roman,Times,serif] text-[20pt] font-bold leading-tight">
          {claimantName || "Claimant"}
        </h2>

        <p className="mt-10 font-[family-name:Times_New_Roman,Times,serif] text-[10pt] text-[#444444]">
          Prepared by
        </p>
        <p className="mt-1 font-[family-name:Times_New_Roman,Times,serif] text-[13pt] font-bold">
          {doctorName}
        </p>
        {qualifications ? (
          <p className={cn(MLR_BODY, "mt-1")}>{qualifications}</p>
        ) : null}
        <p className={cn(MLR_BODY, "mt-4")}>
          Date of report  ·  {reportDate}
        </p>

        <div className="mt-auto border-t border-[#c8c8c8] pt-3">
          <p className="font-[family-name:Times_New_Roman,Times,serif] text-[9pt] leading-snug text-[#444444]">
            {COVER_DISCLAIMER}
          </p>
        </div>
      </div>
    </MlrSheet>
  );
}

type ContentsProps = {
  claimantName: string;
  doctorName: string;
  reportDate: string;
  pageNumber: number;
  totalPages: number;
  onJump: (sectionNumber: number) => void;
  /**
   * Titles come from the section model with the rest of the report, so the
   * contents page cannot list a section the document does not render.
   * (13-09-2026)
   */
  sections: ReadonlyArray<{ number: number; id: string; title: string }>;
};

export function MlrContentsPage({
  claimantName,
  doctorName,
  reportDate,
  pageNumber,
  totalPages,
  onJump,
  sections,
}: ContentsProps) {
  return (
    <MlrSheet
      id="mlr-contents"
      pageNumber={pageNumber}
      totalPages={totalPages}
      claimantName={claimantName}
      doctorName={doctorName}
      reportDate={reportDate}
    >
      <h2 className="font-[family-name:Times_New_Roman,Times,serif] text-[14pt] font-bold">
        Contents
      </h2>
      <p className="mt-1 font-[family-name:Times_New_Roman,Times,serif] text-[9.5pt] text-[#444444]">
        Glossary of report sections and page numbers
      </p>
      <div className="mt-4 h-px bg-[#c8c8c8]" />
      <ol className="mt-3 space-y-2">
        {sections.map((section) => (
          <li key={section.id}>
            <button
              type="button"
              onClick={() => onJump(section.number)}
              className="flex w-full items-baseline gap-2 text-left font-[family-name:Times_New_Roman,Times,serif] text-[10pt] text-[#1a1a1a] hover:text-[#444444]"
            >
              <span className="shrink-0">
                {section.number}.  {section.title}
              </span>
              <span
                className="min-w-[1.5rem] flex-1 border-b border-dotted border-[#444444] translate-y-[-4px]"
                aria-hidden
              />
              <span className="shrink-0 tabular-nums">
                {section.number + 2}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </MlrSheet>
  );
}

export function MlrSectionHeading({
  number,
  title,
}: {
  number: number;
  title: string;
}) {
  return (
    <h3 className="font-[family-name:Times_New_Roman,Times,serif] text-[11pt] font-bold text-[#1a1a1a]">
      {number}. {title}
    </h3>
  );
}

export function MlrSubheading({ children }: { children: string }) {
  return (
    <h4 className="mt-4 font-[family-name:Times_New_Roman,Times,serif] text-[10pt] font-bold text-[#1a1a1a]">
      {children}
    </h4>
  );
}

export function MlrNumberLabel({ children }: { children: string }) {
  return (
    <p className="mt-3 font-[family-name:Times_New_Roman,Times,serif] text-[10pt] font-bold text-[#1a1a1a]">
      {children}
    </p>
  );
}

export function MlrStaticParagraph({
  children,
  nested = false,
}: {
  children: string;
  nested?: boolean;
}) {
  return (
    <p className={cn(MLR_BODY_JUSTIFY, "mt-2", nested && "pl-8")}>{children}</p>
  );
}

export function MlrIdentityTable({
  rows,
}: {
  rows: Array<{
    label: string;
    value: string;
    multiline?: boolean;
    onChange?: (value: string) => void;
    onBlur?: () => void;
  }>;
}) {
  return (
    <table className="mt-3 w-full border-collapse">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="border-b border-[#c8c8c8]">
            <th className="w-[45%] py-2 pr-3 text-left align-top font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] font-bold text-[#1a1a1a]">
              {row.label}
            </th>
            <td className="py-2 align-top font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] leading-[1.5] whitespace-pre-wrap text-[#1a1a1a]">
              {row.onChange && row.multiline ? (
                <textarea
                  aria-label={row.label}
                  value={row.value === "—" ? "" : row.value}
                  rows={3}
                  onChange={(event) => row.onChange?.(event.target.value)}
                  onBlur={() => row.onBlur?.()}
                  className="w-full resize-y border-0 bg-transparent p-0 font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] leading-[1.5] text-[#1a1a1a] outline-none"
                />
              ) : row.onChange ? (
                <input
                  aria-label={row.label}
                  value={row.value === "—" ? "" : row.value}
                  onChange={(event) => row.onChange?.(event.target.value)}
                  onBlur={() => row.onBlur?.()}
                  className="w-full border-0 bg-transparent p-0 font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] leading-[1.5] text-[#1a1a1a] outline-none"
                />
              ) : (
                row.value
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
